import { useQuery } from '@tanstack/react-query';
import { StatusBadge } from '@/components/common/Badge';
import { Card, CardHeader } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDateTime } from '@/utils/format';

/**
 * Every status change the student has been through, newest first.
 *
 * Written by the server on each lifecycle action, so "why was this child
 * suspended in March?" has an answer that does not depend on anyone's memory.
 */
export function StudentStatusHistoryPanel({ studentId }: { studentId: string }) {
  const query = useQuery({
    queryKey: queryKeys.students.statusHistory(studentId),
    queryFn: () => studentsApi.statusHistory(studentId),
    enabled: studentId !== '',
  });

  return (
    <Card>
      <CardHeader title="Status history" description="Each suspension, withdrawal, graduation and return." />

      {query.isPending ? (
        <LoadingState label="Loading status history" />
      ) : query.error !== null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <p className="py-6 text-center text-sm text-subtle">No status changes recorded yet.</p>
      ) : (
        <ol className="divide-y divide-line px-5">
          {query.data.map((entry) => (
            <li key={entry.id} className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                {entry.fromStatus !== undefined && (
                  <>
                    <StatusBadge status={entry.fromStatus} />
                    <span className="text-xs text-subtle" aria-label="to">
                      →
                    </span>
                  </>
                )}
                <StatusBadge status={entry.toStatus} />
                <span className="ml-auto text-xs text-subtle">{formatDateTime(entry.changedAt)}</span>
              </div>
              {entry.reason !== undefined && entry.reason !== '' && (
                <p className="mt-1.5 break-words text-sm text-muted">{entry.reason}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
